export default {
  breadcrumbHome: "Home",
  backHome: "Back to the homepage",
  updated: "Last updated: August 2026",
  cta: {
    title: "Explore the Full Spreadsheet",
    subtitle: "Ready to browse the complete, always-updated product spreadsheet?",
    button: "Open the AllChinaBuy Spreadsheet",
  },
  articles: {
    "allchinabuy-spreadsheet-guide": {
      metaTitle: "AllChinaBuy Spreadsheet 2026: The Complete Beginner's Guide",
      metaDescription: "Learn what the AllChinaBuy spreadsheet is, how to use it, what's inside, plus QC, shipping and safety tips for smarter buying.",
      title: "AllChinaBuy Spreadsheet 2026: The Complete Beginner's Guide",
      tag: "Beginner Guide",
      readingTime: "12 min read",
      lead: "The AllChinaBuy spreadsheet has become one of the most talked-about tools for shoppers who order products from China through the AllChinaBuy agent. In this guide we explain what it is, how to use it, and how to get more value from your finds, quality checks and shipping decisions — all in plain language.",
      sections: [
        {
          h: "What Is the AllChinaBuy Spreadsheet?",
          p: [
            "The AllChinaBuy spreadsheet is a community-compiled collection of curated product links and finds, organized into categories such as shoes, hoodies, t-shirts, jackets, bags and more. Instead of scrolling endlessly through storefronts, shoppers use it as a shortcut to discover items that other buyers have already found and shared.",
            "Think of it as a living shopping list. It brings product links, basic references and category groupings together in one place, so you can compare options and move faster from browsing to ordering.",
          ],
        },
        {
          h: "Why Shoppers Use It",
          p: [
            "The main value is organization and time saved. The spreadsheet groups finds by category, which makes it easier to research a specific item type, spot repeat winners, and build a haul without starting from scratch. Many buyers also use it as a starting point before checking quality control photos and shipping options.",
          ],
        },
        {
          h: "How to Use the Spreadsheet (Step by Step)",
          p: [
            "Start with the category that matches what you want, then work through a simple routine. Open a category and skim the listed finds, then open a product link and read the seller's details and reviews. Check size, color and material before adding to your cart. Review quality control photos once your item arrives at the warehouse, and compare shipping lines and costs before you consolidate and ship.",
          ],
        },
        {
          h: "What's Inside: Categories and Finds",
          p: [
            "The spreadsheet typically covers a wide range of categories — shoes and sneakers, hoodies and sweaters, t-shirts, jackets, pants and shorts, headwear, matching sets, underwear, jerseys, accessories, bags, belts, perfumes and a catch-all for other items. Browsing by category keeps the list manageable and makes it easy to return to a specific section later.",
          ],
        },
        {
          h: "Reading QC Photos and Quality Checks",
          p: [
            "Quality control photos are your best chance to catch problems before an item ships. Look at the actual color and material, check stitching and logos, confirm the size tag matches what you ordered, and watch for obvious defects or differences from the listing. If something looks off, ask for another photo or a return while the item is still in the warehouse.",
          ],
        },
        {
          h: "Shipping Costs and Ways to Save",
          p: [
            "Shipping cost depends on weight, dimensions and the line you choose. Lighter lines cost less but may be slower or offer less protection, while faster lines cost more. To save, keep your haul compact, remove unnecessary packaging, and consolidate several items into one parcel instead of shipping them separately.",
          ],
        },
        {
          h: "Safety and What to Check",
          p: [
            "Always use the official site and double-check the URL before logging in or paying. Be cautious with unknown sellers, and never rely on coupons or discount codes you cannot verify. This guide is independent and informational — it is not affiliated with AllChinaBuy, and prices, fees and policies can change at any time.",
          ],
        },
      ],
      faq: [
        { q: "Is the AllChinaBuy spreadsheet free?", a: "Browsing a community-compiled spreadsheet is generally free, but individual products, shipping and fees are separate. Always confirm current pricing on the official site." },
        { q: "Do I need a spreadsheet to buy from AllChinaBuy?", a: "No. The spreadsheet is a convenience that helps you discover and organize finds. You can still browse and order directly without it." },
        { q: "How often is the spreadsheet updated?", a: "Update frequency varies because it is community-maintained. Treat product links and prices as a starting point and verify them before ordering." },
      ],
    },
    "allchinabuy-finds": {
      metaTitle: "AllChinaBuy Finds 2026: How to Discover the Best Products",
      metaDescription: "A practical guide to discovering, organizing and evaluating AllChinaBuy finds across shoes, hoodies, bags and more for smarter hauls.",
      title: "AllChinaBuy Finds 2026: How to Discover the Best Products",
      tag: "Product Research",
      readingTime: "9 min read",
      lead: "Finding genuinely good products on a large agent platform can feel like searching for a needle in a haystack. This guide breaks down how to discover solid AllChinaBuy finds, how to organize them, and how to tell a real winner from a risky purchase.",
      sections: [
        {
          h: "What Counts as a Good Find?",
          p: [
            "A good find is not just a cheap price — it is a product with consistent quality, clear photos, honest seller reviews and a reasonable shipping profile. The best finds solve a specific need and hold up under inspection, rather than simply being the first item that appears in a search.",
          ],
        },
        {
          h: "Where to Look First",
          p: [
            "Start with the spreadsheet categories that match your interest — shoes, hoodies, t-shirts, jackets, bags and accessories are popular starting points. Skim the listed links, note which sellers appear repeatedly, and treat those recurring names as signals worth investigating more closely.",
          ],
        },
        {
          h: "How to Evaluate a Find Before Ordering",
          p: [
            "Read the product title and description carefully, then check the seller's feedback and any buyer reviews. Look at every photo, confirm the size and color options, and compare the item against similar listings to spot inflated prices. If a deal looks too good to be true, it usually is.",
          ],
        },
        {
          h: "Organizing Your Own Finds",
          p: [
            "Keep a simple personal list with the product link, size, color and a short note about why you saved it. This makes it easier to compare options later, build a balanced haul, and revisit items when you are ready to order instead of losing them in your browser history.",
          ],
        },
      ],
      faq: [
        { q: "How do I know a find is trustworthy?", a: "Look for clear photos, a detailed description and real buyer reviews. Cross-check the seller and treat unusually low prices with caution." },
        { q: "Can I share my own finds?", a: "Yes, many community spreadsheets welcome contributions. Just make sure any link you share is accurate and safe to open." },
      ],
    },
    "allchinabuy-qc-guide": {
      metaTitle: "AllChinaBuy QC Guide: How to Read QC Photos Like a Pro",
      metaDescription: "Learn how to read AllChinaBuy QC photos, spot defects, check sizing, logos and stitching, and what to do when something looks wrong.",
      title: "AllChinaBuy QC Guide: How to Read QC Photos Like a Pro",
      tag: "Quality Control",
      readingTime: "10 min read",
      lead: "Quality control photos are your best line of defense against a disappointing order. This guide teaches you what to look for in AllChinaBuy QC photos, the most common red flags, and how to act before your item leaves the warehouse.",
      sections: [
        {
          h: "Why QC Photos Matter",
          p: [
            "Once your item reaches the warehouse, QC photos give you a real look at what you actually ordered — not the idealized listing images. Catching a problem here means you can request a replacement or return while the item is still in China, saving you money and frustration later.",
          ],
        },
        {
          h: "What to Check on Every Photo",
          p: [
            "Start with the overall color and material, then move to the details: stitching, logos, zippers and hardware. Confirm the size tag matches what you ordered, check for stains or tears, and compare the item side by side with the original listing photos.",
          ],
        },
        {
          h: "Common Red Flags",
          p: [
            "Watch for off-color items, misaligned or sloppy logos, loose threads, crooked seams, wrong sizing labels and obvious differences from the listing. For footwear, check both shoes for matching details and correct box labels.",
          ],
        },
        {
          h: "What to Do When Something Is Off",
          p: [
            "If you spot a problem, request additional photos to confirm before deciding. Most agents let you request an exchange or return while the item is still at the warehouse, which is far cheaper than shipping it back internationally after delivery.",
          ],
        },
      ],
      faq: [
        { q: "Can I request extra QC photos?", a: "In many cases yes. If the standard photos do not show a detail you need, ask for a closer or additional angle before the item ships." },
        { q: "What if the QC photo looks different from the listing?", a: "Flag it immediately and ask for clarification. You can usually request a return or exchange while the item is still in the warehouse." },
      ],
    },
    "allchinabuy-shipping-cost": {
      metaTitle: "AllChinaBuy Shipping Cost 2026: Complete Guide & Ways to Save",
      metaDescription: "Understand how AllChinaBuy shipping cost is calculated, the difference between weight and volumetric weight, and practical ways to reduce your shipping bill.",
      title: "AllChinaBuy Shipping Cost 2026: Complete Guide & Ways to Save",
      tag: "Shipping",
      readingTime: "11 min read",
      lead: "Shipping is often the biggest surprise in a haul's final cost. This guide explains how AllChinaBuy shipping cost is calculated, the difference between actual and volumetric weight, and concrete steps you can take to pay less.",
      sections: [
        {
          h: "How Shipping Cost Is Calculated",
          p: [
            "Shipping cost is driven mainly by weight, package dimensions and the line you choose. Some lines charge by actual weight, while others charge by volumetric weight — the space your parcel takes up — and you are typically billed for whichever is higher.",
          ],
        },
        {
          h: "Actual vs Volumetric Weight",
          p: [
            "Actual weight is simply how heavy your items are. Volumetric weight is calculated from the box dimensions, so a large but light item can cost more than a small heavy one. Bulky items like jackets, bags and shoe boxes often push volumetric weight up.",
          ],
        },
        {
          h: "Choosing a Shipping Line",
          p: [
            "Faster lines cost more but deliver quickly, while economy lines are cheaper but slower and sometimes offer less protection. Match the line to your budget and how urgently you need the items, and read the line's limits and restrictions before selecting.",
          ],
        },
        {
          h: "Practical Ways to Save",
          p: [
            "Consolidate multiple items into one parcel instead of shipping separately, remove unnecessary packaging and shoe boxes to cut volume, and avoid splitting a small haul into many tiny shipments. Every bit of saved volume and weight lowers the final cost.",
          ],
        },
      ],
      faq: [
        { q: "Why is my shipping so expensive?", a: "Volumetric weight is a common culprit — bulky items inflate the charge even when they are light. Removing packaging and consolidating usually helps." },
        { q: "Is a cheaper shipping line safe?", a: "Cheaper lines can be slower and offer less protection. Choose based on value, urgency and how fragile your items are." },
      ],
    },
    "allchinabuy-warehouse": {
      metaTitle: "AllChinaBuy Warehouse 2026: Storage, QC, Packing & Shipping",
      metaDescription: "How the AllChinaBuy warehouse works — storage, quality control, consolidation, packing and the journey to international shipping.",
      title: "AllChinaBuy Warehouse 2026: Storage, QC, Packing & Shipping",
      tag: "Warehouse",
      readingTime: "9 min read",
      lead: "The warehouse is where your items wait between arriving from the seller and shipping to you. Understanding how storage, QC, consolidation and packing work helps you plan a smoother, cheaper haul.",
      sections: [
        {
          h: "What the Warehouse Does",
          p: [
            "After a seller ships your item, it lands at the warehouse where staff log it, store it and typically take QC photos. You can then review the photos, decide whether to keep the item, and group it with the rest of your haul.",
          ],
        },
        {
          h: "Storage and Free Periods",
          p: [
            "Warehouses usually offer a free storage window before fees apply, letting you accumulate items over time. Check the current limits and plan your ordering so you do not let items sit past the free period.",
          ],
        },
        {
          h: "Consolidation and Packing",
          p: [
            "When you are ready, the warehouse consolidates your items into one parcel. You can often request extra protection, box removal or a specific packing approach. Good packing protects fragile items and can also reduce the parcel's size.",
          ],
        },
        {
          h: "From Warehouse to International Shipping",
          p: [
            "Once packed, your parcel moves to the shipping line you selected. The warehouse-to-doorstep journey includes export handling, international transit and local delivery, so tracking and patience are both important.",
          ],
        },
      ],
      faq: [
        { q: "How long can I store items?", a: "Free storage windows vary. Check current limits so items do not accrue fees while you build your haul." },
        { q: "Can I remove packaging to save money?", a: "Many agents allow box removal or lighter packing, which can reduce volumetric weight and shipping cost." },
      ],
    },
    "allchinabuy-review": {
      metaTitle: "AllChinaBuy Review 2026: Fees, Shipping, QC & Pros/Cons",
      metaDescription: "An independent AllChinaBuy review covering how the agent works, typical fees, shipping, QC photos, and the main pros and cons for buyers.",
      title: "AllChinaBuy Review 2026: Fees, Shipping, QC & Pros/Cons",
      tag: "Review",
      readingTime: "12 min read",
      lead: "AllChinaBuy is a shopping agent that helps buyers order from Chinese platforms and ship internationally. This independent review walks through how it works, what to expect with fees and shipping, and the trade-offs to weigh before you start.",
      sections: [
        {
          h: "How AllChinaBuy Works",
          p: [
            "You paste a product link into the agent, pay for the item, and the agent purchases it on your behalf. The item is then sent to the agent's warehouse, where you can review QC photos before consolidating and shipping your haul internationally.",
          ],
        },
        {
          h: "Typical Fees",
          p: [
            "Fees generally include the product cost, a service or handling fee, and international shipping. Some agents add currency conversion or payment fees, so it is worth reading the fee breakdown before you commit to a large order.",
          ],
        },
        {
          h: "Shipping and QC Experience",
          p: [
            "QC photos are a major advantage, letting you catch issues early. Shipping offers multiple lines at different speeds and price points, so the final cost depends heavily on how you consolidate and which line you pick.",
          ],
        },
        {
          h: "Pros and Cons at a Glance",
          p: [
            "The pros include easier purchasing, QC photos, warehouse consolidation and a wide range of shipping lines. The cons are the added fees, the time involved, and the need to verify details yourself since information can change.",
          ],
        },
      ],
      faq: [
        { q: "Is AllChinaBuy worth the fees?", a: "For buyers who value QC photos and consolidation, the convenience can be worth it. Compare total cost against alternatives before committing." },
        { q: "Are there hidden costs?", a: "Read the fee breakdown carefully — service, handling, payment and shipping fees can add up beyond the item price." },
      ],
    },
    "allchinabuy-legit": {
      metaTitle: "Is AllChinaBuy Legit in 2026? Safety & Buyer Guide",
      metaDescription: "An independent look at whether AllChinaBuy is legit, how to shop safely, spot phishing, protect payments and avoid common buyer scams.",
      title: "Is AllChinaBuy Legit in 2026? Safety & Buyer Guide",
      tag: "Safety",
      readingTime: "10 min read",
      lead: "When you are sending money to an agent overseas, trust matters. This guide explains how to evaluate whether AllChinaBuy is legit, how to protect yourself, and the safety habits every buyer should follow.",
      sections: [
        {
          h: "What to Know Before You Trust an Agent",
          p: [
            "A legit agent has a clear website, transparent fees and a track record of fulfilled orders. This site is an independent informational resource and is not affiliated with AllChinaBuy, so always confirm details on the official site before purchasing.",
          ],
        },
        {
          h: "Verify the Website and URL",
          p: [
            "Always double-check the URL before logging in or paying, and be wary of lookalike sites and messages that pressure you to act fast. Bookmark the official address and type it directly instead of following links from unknown emails.",
          ],
        },
        {
          h: "Protect Your Payment and Account",
          p: [
            "Use a secure payment method, enable any available two-factor authentication, and never share your login or verification codes. Keep records of your orders and payments so you can dispute issues if anything goes wrong.",
          ],
        },
        {
          h: "Avoid Common Scams",
          p: [
            "Beware of fake coupon codes, unsolicited seller offers and too-good-to-be-true deals. Coupons and discounts you cannot verify on the official site should be ignored, and you should never send payment outside the official checkout.",
          ],
        },
      ],
      faq: [
        { q: "Is this site affiliated with AllChinaBuy?", a: "No. This is an independent informational resource. Always verify prices, policies and safety details on the official site." },
        { q: "How can I spot a phishing site?", a: "Check the URL character by character, look for secure browsing indicators, and avoid links from unexpected emails or messages." },
      ],
    },
    "allchinabuy-coupon": {
      metaTitle: "AllChinaBuy Coupon & Discount Code 2026: Savings Guide",
      metaDescription: "How AllChinaBuy coupons and discount codes work, where to find legit offers, and how to save without falling for fake deals.",
      title: "AllChinaBuy Coupon & Discount Code 2026: Savings Guide",
      tag: "Savings",
      readingTime: "8 min read",
      lead: "Everyone loves a discount, but coupon hunting comes with risk. This guide explains how AllChinaBuy coupons and discount codes work, where legitimate offers come from, and how to avoid fake deals that waste your time or put you at risk.",
      sections: [
        {
          h: "How Coupons and Discount Codes Work",
          p: [
            "Discount codes are typically applied at checkout to reduce shipping, service fees or the item total. Offers can be seasonal, tied to new-user signups, or shared through official channels — and they usually come with conditions and expiry dates.",
          ],
        },
        {
          h: "Where Legitimate Offers Come From",
          p: [
            "The safest place to find real offers is the official site or its verified channels. Avoid third-party sites that promise huge discounts or list dozens of unverified codes, as these are often outdated or misleading.",
          ],
        },
        {
          h: "How to Save Without a Code",
          p: [
            "Even without a coupon, you can save by consolidating shipments, removing excess packaging, choosing an economy line and comparing items before you order. Smart packing and planning often outweigh a small discount code.",
          ],
        },
        {
          h: "Avoiding Fake Deals",
          p: [
            "This site does not list specific coupon codes, and you should never trust a code you cannot verify on the official site. Be wary of any offer that asks you to click a suspicious link or pay outside the normal checkout.",
          ],
        },
      ],
      faq: [
        { q: "Where can I find working coupon codes?", a: "Check the official site and its verified channels. Treat third-party code lists with caution, as they are often outdated." },
        { q: "Does this site provide coupon codes?", a: "No. This is an independent informational resource and does not publish specific, unverifiable coupon codes." },
      ],
    },
    "allchinabuy-buying-guide": {
      metaTitle: "AllChinaBuy Buying Guide: Step-by-Step for Beginners",
      metaDescription: "A beginner's step-by-step AllChinaBuy buying guide covering ordering, QC, consolidation, shipping and common first-time mistakes.",
      title: "AllChinaBuy Buying Guide: Step-by-Step for Beginners",
      tag: "Buying Guide",
      readingTime: "11 min read",
      lead: "Your first order through a shopping agent can feel confusing. This beginner's buying guide walks through the whole AllChinaBuy process step by step, from finding a product to receiving your parcel, so you can order with confidence.",
      sections: [
        {
          h: "Step 1: Find and Add a Product",
          p: [
            "Start by choosing a product, then copy its link into the agent. Confirm the size, color, quantity and any variations before you pay, since these details are easy to get wrong on a first order.",
          ],
        },
        {
          h: "Step 2: Pay and Wait for the Warehouse",
          p: [
            "After payment, the agent purchases the item and the seller ships it to the warehouse. This stage can take a few days, so track the status and be patient while your item is in transit.",
          ],
        },
        {
          h: "Step 3: Review QC Photos",
          p: [
            "When the item arrives, review the QC photos carefully. Check color, size, stitching and any defects. If something is wrong, request more photos or a return before it ships internationally.",
          ],
        },
        {
          h: "Step 4: Consolidate and Ship",
          p: [
            "Once you are happy with your items, consolidate them into one parcel, choose a shipping line that fits your budget and timeline, and pay the shipping cost. Then track the parcel as it makes its way to you.",
          ],
        },
        {
          h: "Common First-Time Mistakes",
          p: [
            "Rushing past size checks, ignoring QC photos, shipping tiny orders separately and choosing the fastest line by default are all common mistakes. Slow down, verify details, and consolidate to keep costs down.",
          ],
        },
      ],
      faq: [
        { q: "How long does a first order take?", a: "It varies by seller, warehouse processing and shipping line, but expect several days to a few weeks from order to delivery." },
        { q: "What if my item arrives damaged?", a: "Catch it in QC photos and request a return or exchange while it is still at the warehouse — it is much cheaper than shipping it back later." },
      ],
    },
    "how-to-use-allchinabuy": {
      metaTitle: "How to Use the AllChinaBuy Spreadsheet: A Practical Walkthrough",
      metaDescription: "A practical walkthrough on how to use the AllChinaBuy spreadsheet to research products, organize finds and plan a smarter haul.",
      title: "How to Use the AllChinaBuy Spreadsheet: A Practical Walkthrough",
      tag: "How-To",
      readingTime: "8 min read",
      lead: "The spreadsheet is only useful if you know how to navigate it. This practical walkthrough shows you how to use the AllChinaBuy spreadsheet to research products, keep your finds organized and plan a haul that saves you money.",
      sections: [
        {
          h: "Getting Oriented",
          p: [
            "Open the spreadsheet and scan the category tabs first. Shoes, hoodies, t-shirts, jackets, bags and accessories are common starting points. Understanding the layout before you dig in saves you from endless scrolling.",
          ],
        },
        {
          h: "Browsing a Category",
          p: [
            "Pick one category and skim the listed links. Note the sellers and items that stand out, and open a few product pages to compare. The goal is to build a shortlist, not to click everything at once.",
          ],
        },
        {
          h: "Building Your Shortlist",
          p: [
            "Copy the links you like into your own list with a note on size, color and why you saved them. A personal shortlist keeps your research organized and makes it easy to revisit items when you are ready to order.",
          ],
        },
        {
          h: "From Shortlist to Order",
          p: [
            "When you are ready, move your shortlist into the agent, verify each item's details, and order. Then follow the standard QC and shipping steps to complete your haul.",
          ],
        },
      ],
      faq: [
        { q: "Is the spreadsheet hard to learn?", a: "No. It is just organized lists of product links. A few minutes of browsing is enough to understand the layout." },
        { q: "Can I use the spreadsheet without an account?", a: "Browsing and reading the guides requires no account. Ordering itself happens on the agent's official platform." },
      ],
    },
    "allchinabuy-vs-superbuy": {
      metaTitle: "AllChinaBuy vs Superbuy: Which Agent Is Right for You?",
      metaDescription: "An independent comparison of AllChinaBuy vs Superbuy covering fees, QC, shipping and usability to help you choose the right shopping agent.",
      title: "AllChinaBuy vs Superbuy: Which Agent Is Right for You?",
      tag: "Comparison",
      readingTime: "10 min read",
      lead: "Choosing between shopping agents can be tricky. This independent comparison looks at AllChinaBuy vs Superbuy across the factors that matter most — fees, QC photos, shipping options and overall ease of use — so you can pick the agent that fits your needs.",
      sections: [
        {
          h: "What Both Agents Do",
          p: [
            "Both AllChinaBuy and Superbuy act as middlemen that buy products on your behalf, store them in a warehouse, provide QC photos and ship your haul internationally. The core workflow is similar, which is why the differences come down to fees, options and experience.",
          ],
        },
        {
          h: "Fees and Pricing",
          p: [
            "Fees vary and change over time, so this guide does not publish fixed numbers. The best approach is to compare the full breakdown — service fees, payment fees, currency conversion and shipping — for your specific order before deciding.",
          ],
        },
        {
          h: "QC, Shipping and Usability",
          p: [
            "QC photo quality, available shipping lines and how easy the platform is to use all affect your experience. Look at how clearly each agent presents QC photos, how many shipping options you have, and how responsive their support is.",
          ],
        },
        {
          h: "How to Choose",
          p: [
            "If you value a large community and established guides, one agent may suit you better; if you prefer a specific shipping line or lower service fee, the other might win. Run a small test order and compare the real cost and experience before committing to a big haul.",
          ],
        },
      ],
      faq: [
        { q: "Which agent is cheaper?", a: "It depends on your order. Compare the full fee and shipping breakdown for your specific items rather than relying on a single headline price." },
        { q: "Can I use both agents?", a: "Yes, many buyers use different agents for different orders. A small test order is the best way to compare the real experience." },
      ],
    },
  },
};